import { endOfDay, format, subYears } from 'date-fns';

export const QLD_STAGE_KEYS = ['lln', 'eoi', 'assessment1', 'assessment2', 'assessment3', 'assessment4', 'declaration'];

export const QLD_STAGE_LABELS = {
    lln: 'LLND',
    eoi: 'EOI',
    assessment1: 'Assessment 1',
    assessment2: 'Assessment 2',
    assessment3: 'Assessment 3',
    assessment4: 'Assessment 4',
    declaration: 'Declaration',
};

export const isQldAssessmentStage = (stageKey = '') => /^assessment\d+$/.test(stageKey);

export const toTitleCase = (value = '') => value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());

export const formatQldNameInput = (value = '') => value
    .toString()
    .replace(/\s+/g, ' ')
    .replace(/(^\w)|(\s+\w)/g, (match) => match.toUpperCase());

export const isValidEmail = (value = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

export const buildQldFullName = (studentDoc = {}) => (
    [studentDoc.firstName, studentDoc.middleName, studentDoc.lastName]
        .filter(Boolean)
        .join(' ')
        .trim()
);

export const buildInitialQldStages = () => (
    QLD_STAGE_KEYS.reduce((acc, key, index) => ({
        ...acc,
        [key]: {
            unlocked: index === 0,
            completed: false,
            completedAt: '',
            trainerReviewed: false,
            reviewStatus: '',
        }
    }), {})
);

export const buildQldLinkPayload = (trainerId) => {
    const now = new Date();
    const expiresAt = endOfDay(now);
    const linkId = `${trainerId}-${format(now, 'yyyyMMdd')}-${Math.random().toString(36).slice(2, 8)}`;

    return {
        linkId,
        trainerId,
        createdAt: now.toISOString(),
        createdDateLabel: format(now, 'dd MMM yyyy'),
        expiresAt: expiresAt.toISOString(),
        active: true,
        url: `${window.location.origin}/qldEoi?linkId=${linkId}`,
    };
};

export const buildQldStudentPayload = ({
    linkId,
    trainerId,
    firstName,
    middleName,
    lastName,
    email,
    dob,
    mobile = '',
    address = '',
    streetNumber = '',
    streetName = '',
    suburb = '',
    state = '',
    postcode = '',
}) => ({
    linkId,
    trainerId,
    firstName: toTitleCase(firstName),
    middleName: toTitleCase(middleName || ''),
    lastName: toTitleCase(lastName),
    email,
    emailNormalized: (email || '').trim().toLowerCase(),
    dob,
    mobile: String(mobile || '').trim(),
    dobKey: dob ? format(new Date(dob), 'yyyy-MM-dd') : '',
    underMinimumAge: dob ? new Date(dob).getTime() > subYears(new Date(), 13).getTime() : false,
    address: String(address || '').trim(),
    streetNumber: String(streetNumber || '').trim(),
    streetName: String(streetName || '').trim(),
    suburb: toTitleCase(suburb || ''),
    state: String(state || '').trim(),
    postcode: String(postcode || '').trim(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    stages: buildInitialQldStages(),
    responses: {},
    trainerResponses: {},
});

export const getCurrentStudentStage = (studentDoc) => {
    if (!studentDoc?.stages) {
        return null;
    }

    return QLD_STAGE_KEYS.find((key) => {
        const stage = studentDoc.stages[key];
        return stage?.unlocked && !stage?.completed;
    }) || null;
};

export const getNextLockedStage = (studentDoc) => {
    if (!studentDoc?.stages) {
        return null;
    }

    return QLD_STAGE_KEYS.find((key) => !studentDoc.stages[key]?.unlocked) || null;
};

export const isQldLinkActive = (linkDoc) => {
    if (!linkDoc?.active || !linkDoc?.expiresAt) {
        return false;
    }

    return new Date(linkDoc.expiresAt).getTime() >= Date.now();
};

export const buildStageCompletionPatch = (studentDoc, stageKey, responseData) => ({
    responses: {
        ...(studentDoc.responses || {}),
        [stageKey]: isQldAssessmentStage(stageKey)
            ? {
                ...(responseData || {}),
                hasCompletedAssessment: true,
                answeringIncorrect: false,
                manualRetryQuestionIndexes: studentDoc?.stages?.[stageKey]?.reviewStatus === 'needs_resubmission'
                    ? (studentDoc.responses?.[stageKey]?.manualRetryQuestionIndexes || [])
                    : [],
            }
            : responseData,
    },
    stages: {
        ...(studentDoc.stages || {}),
        [stageKey]: {
            ...(studentDoc.stages?.[stageKey] || {}),
            completed: true,
            completedAt: new Date().toISOString(),
            trainerReviewed: false,
            reviewStatus: stageKey === 'declaration'
                ? 'student_submitted'
                : 'submitted_for_review',
        },
        ...(stageKey === 'lln'
            ? {
                eoi: {
                    ...(studentDoc.stages?.eoi || {}),
                    unlocked: true,
                }
            }
            : {})
    },
    updatedAt: new Date().toISOString(),
});

export const buildStageUnlockPatch = (studentDoc, stageKey) => ({
    stages: {
        ...(studentDoc.stages || {}),
        [stageKey]: {
            ...(studentDoc.stages?.[stageKey] || {}),
            unlocked: true,
        }
    },
    updatedAt: new Date().toISOString(),
});
